import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, TrendingUp, Landmark, Wallet } from "lucide-react";
import { useLang } from "@/LanguageContext";
import { useTexts } from "@/TextsContext";
import t from "@/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArgumentsSection() {
  const { lang } = useLang();
  const tx = t[lang];
  const { texts } = useTexts();
  const argsCfg = texts?.[lang]?.args;
  const tag = argsCfg?.tag || tx.args_tag;
  const h2 = argsCfg?.h2 || tx.args_h2;
  const [bgUrl, setBgUrl] = useState(null);

  useEffect(() => {
    axios.get(`${API}/media/slot/section_arguments/random?fallback_slot=hero`)
      .then(({ data }) => {
        if (data?.url && !data?.no_bg) {
          setBgUrl(data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        } else {
          setBgUrl(null);
        }
      })
      .catch(() => {});
  }, []);

  const fallbackItems = [
    { title: tx.arg1_title, text: tx.arg1_text, hl: tx.arg1_hl },
    { title: tx.arg2_title, text: tx.arg2_text, hl: tx.arg2_hl },
    { title: tx.arg3_title, text: tx.arg3_text, hl: tx.arg3_hl },
    { title: tx.arg4_title, text: tx.arg4_text, hl: tx.arg4_hl },
  ];
  const icons = [TrendingUp, Shield, Landmark, Wallet];
  const items = (argsCfg?.items?.length === 4 ? argsCfg.items : fallbackItems).map((it, i) => ({
    num: `0${i + 1}`,
    icon: icons[i],
    title: it.title,
    text: it.text,
    hl: it.hl,
  }));

  const bgStyle = bgUrl
    ? { backgroundImage: `linear-gradient(rgba(12,22,32,0.93), rgba(12,22,32,0.93)), url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
    : undefined;

  return (
    <section id="argumente" data-testid="arguments-section" className="py-16 sm:py-24 lg:py-32 bg-[#0C1620] relative" style={bgStyle}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 sm:mb-16">
          <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.25em] text-[#C5993A] mb-3 sm:mb-4">{tag}</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl tracking-tight font-light text-white">{h2}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8 lg:gap-12">
          {items.map((arg) => {
            const Icon = arg.icon;
            return (
              <div key={arg.num} data-testid={`argument-card-${arg.num}`} className="group relative bg-[#12202f] border border-[#3A4F60]/50 rounded-lg p-5 sm:p-8 lg:p-10 hover:-translate-y-1 hover:border-[#C5993A]/50 transition-all duration-300 overflow-hidden">
                <span aria-hidden="true" className="absolute -top-4 sm:-top-6 right-3 sm:right-6 font-heading font-black leading-none select-none pointer-events-none text-transparent transition-all duration-500 text-[140px] sm:text-[180px] lg:text-[220px]"
                  style={{ WebkitTextStroke: "2px rgba(197,153,58,0.45)", textShadow: "0 0 24px rgba(197,153,58,0.08)" }}
                >{arg.num}</span>
                <div className="relative z-10">
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-[#C5993A]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#C5993A]" />
                    </div>
                    <span className="font-heading text-sm sm:text-lg text-[#C5993A] font-semibold">{arg.hl}</span>
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl lg:text-2xl font-medium text-white mb-3 sm:mb-4">{arg.title}</h3>
                  <p className="text-[#8899AA] leading-relaxed text-xs sm:text-sm lg:text-base font-light">{arg.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
